import { useState } from "react";
import { ChevronDown, Check, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { isAuthenticated, getUserInfo } from "@/lib/utils/auth-storage";
import { useT } from "@/lib/i18n";
import { STORAGE_KEYS } from "@/lib/constants";
import {
  submitCustomerProfile,
  type CustomerProfileService,
  type CustomerProfileBudget,
  type CustomerProfileLanguage,
} from "@/lib/api/customer-profile";

// --- storage helpers ---

const FORM_KEY_SESSION = "oranai_lead_submitted";
const FORM_KEY_USER = (id: number) => `oranai_lead_submitted_${id}`;

export function hasSubmittedForm(): boolean {
  try {
    const userInfo = getUserInfo();
    if (isAuthenticated() && userInfo) {
      return localStorage.getItem(FORM_KEY_USER(userInfo.id)) === "1";
    }
    return sessionStorage.getItem(FORM_KEY_SESSION) === "1";
  } catch {
    return false;
  }
}

export function markFormSubmitted(): void {
  try {
    const userInfo = getUserInfo();
    if (isAuthenticated() && userInfo) {
      localStorage.setItem(FORM_KEY_USER(userInfo.id), "1");
    } else {
      sessionStorage.setItem(FORM_KEY_SESSION, "1");
    }
  } catch {}
}

// --- constants ---

const SERVICES = [
  { value: "创意内容制作", en: "Creative Content Production", zh: "创意内容制作" },
  { value: "品牌整合营销：国内", en: "Integrated Brand Marketing: Domestic", zh: "品牌整合营销：国内" },
  { value: "品牌整合营销：出海", en: "Integrated Brand Marketing: Global", zh: "品牌整合营销：出海" },
  { value: "GEO（AI搜索优化）", en: "GEO (AI Search Optimization)", zh: "GEO（AI搜索优化）" },
  { value: "AI+营销定制化产品", en: "AI+ Custom Marketing Products", zh: "AI+营销定制化产品" },
  { value: "OranAI产品技术服务", en: "OranAI Product & Tech Services", zh: "OranAI产品技术服务" },
  { value: "其他", en: "Other", zh: "其他" },
] as const;

const BUDGETS = [
  { value: "0~5w" as CustomerProfileBudget, label: "0~5w" },
  { value: "5~10w" as CustomerProfileBudget, label: "5~10w" },
  { value: "10~20w" as CustomerProfileBudget, label: "10~20w" },
  { value: "20~50w" as CustomerProfileBudget, label: "20~50w" },
  { value: "50w~" as CustomerProfileBudget, label: "50w~>" },
] as const;

// --- types ---

interface FormData {
  name: string;
  company: string;
  position: string;
  industry: string;
  country: string;
  phone: string;
  email: string;
  services: CustomerProfileService[];
  budget: CustomerProfileBudget;
  otherServiceRemark: string;
}

type FormErrors = Partial<Record<keyof FormData | "_form", string>>;

const EMPTY: FormData = {
  name: "",
  company: "",
  position: "",
  industry: "",
  country: "",
  phone: "",
  email: "",
  services: [],
  budget: "0~5w",
  otherServiceRemark: "",
};

// --- helper to get current language ---

function getCurrentLanguage(): CustomerProfileLanguage {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.LANGUAGE);
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed === 'zh' ? 'zh-CN' : 'en-US';
    }
  } catch {}
  return 'en-US';
}

// --- component ---

export interface LeadFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmitted?: () => void;
}

export function LeadFormDialog({ open, onOpenChange, onSubmitted }: LeadFormDialogProps) {
  const t = useT();
  const [form, setForm] = useState<FormData>(EMPTY);
  const [errors, setErrors] = useState<FormErrors>({});
  const [servicesOpen, setServicesOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showOtherRemark, setShowOtherRemark] = useState(false);

  const setField = (key: keyof FormData, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const setServices = (services: CustomerProfileService[]) => {
    setForm((prev) => ({ ...prev, services }));
    setShowOtherRemark(services.includes("其他"));
    setErrors((prev) => ({ ...prev, services: undefined }));
  };

  const toggleService = (value: CustomerProfileService) => {
    const newServices = form.services.includes(value)
      ? form.services.filter((s) => s !== value)
      : [...form.services, value];
    setServices(newServices);
  };

  const validate = (): boolean => {
    const required = t("Required", "必填");
    const errs: FormErrors = {};

    if (!form.name.trim()) errs.name = required;
    if (!form.company.trim()) errs.company = required;
    if (!form.position.trim()) errs.position = required;
    if (!form.country.trim()) errs.country = required;

    // Phone and email: at least one required
    if (!form.phone.trim() && !form.email.trim()) {
      errs.phone = t("Phone and email required", "手机和邮箱至少填写一个");
      errs.email = required;
    } else {
      if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
        errs.email = t("Invalid email format", "邮箱格式不正确");
      }
    }

    if (form.services.length === 0) errs.services = required;
    if (!form.budget) errs.budget = required;

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    setErrors({});

    try {
      await submitCustomerProfile({
        name: form.name.trim(),
        language: getCurrentLanguage(),
        company: form.company.trim(),
        position: form.position.trim(),
        industry: form.industry.trim() || undefined,
        country: form.country.trim(),
        phone: form.phone.trim() || undefined,
        email: form.email.trim() || undefined,
        services: form.services,
        otherServiceRemark: form.services.includes("其他") && form.otherServiceRemark.trim()
          ? form.otherServiceRemark.trim()
          : undefined,
        budget: form.budget,
      });

      markFormSubmitted();
      onSubmitted?.();
      onOpenChange(false);
      setForm(EMPTY);
      setShowOtherRemark(false);
    } catch (error) {
      setErrors({
        _form: error instanceof Error ? error.message : t("Submission failed", "提交失败"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const servicesLabel =
    form.services.length === 0
      ? t("Select services…", "请选择服务…")
      : form.services
          .map((v) => {
            const s = SERVICES.find((x) => x.value === v);
            return s ? t(s.en, s.zh) : v;
          })
          .join(", ");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="landing-v2 sm:max-w-[560px] max-h-[90vh] overflow-y-auto bg-background border border-border rounded-3xl p-8">
        <DialogTitle className="text-xl font-semibold text-foreground">
          {t("Get in Touch", "联系我们")}
        </DialogTitle>
        <p className="text-sm text-muted-foreground -mt-1 mb-2">
          {t(
            "Tell us about yourself and what you're looking for.",
            "告诉我们关于你和你的需求，我们会尽快联系。",
          )}
        </p>

        {errors._form && (
          <p className="text-sm text-destructive mb-4 bg-destructive/10 p-3 rounded-lg">
            {errors._form}
          </p>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* ── Section 1: About you ── */}
          <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground pt-2">
            {t("About You", "关于你")}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label={t("Name", "姓名")} required error={errors.name}>
              <Input
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
                placeholder={t("Your name", "你的姓名")}
                disabled={isSubmitting}
                className="bg-white/5 border-white/10 focus-visible:border-white/25 focus-visible:ring-0 text-foreground placeholder:text-muted-foreground/60"
              />
            </Field>

            <Field label={t("Company", "公司")} required error={errors.company}>
              <Input
                value={form.company}
                onChange={(e) => setField("company", e.target.value)}
                placeholder={t("Company name", "公司名称")}
                disabled={isSubmitting}
                className="bg-white/5 border-white/10 focus-visible:border-white/25 focus-visible:ring-0 text-foreground placeholder:text-muted-foreground/60"
              />
            </Field>

            <Field label={t("Position", "职位")} required error={errors.position}>
              <Input
                value={form.position}
                onChange={(e) => setField("position", e.target.value)}
                placeholder={t("Your title", "你的职位")}
                disabled={isSubmitting}
                className="bg-white/5 border-white/10 focus-visible:border-white/25 focus-visible:ring-0 text-foreground placeholder:text-muted-foreground/60"
              />
            </Field>

            <Field label={t("Industry", "行业领域")} error={errors.industry}>
              <Input
                value={form.industry}
                onChange={(e) => setField("industry", e.target.value)}
                placeholder={t("e.g. Consumer goods", "如：消费品")}
                disabled={isSubmitting}
                className="bg-white/5 border-white/10 focus-visible:border-white/25 focus-visible:ring-0 text-foreground placeholder:text-muted-foreground/60"
              />
            </Field>

            <Field label={t("Country", "国家")} required error={errors.country}>
              <Input
                value={form.country}
                onChange={(e) => setField("country", e.target.value)}
                placeholder={t("Country / Region", "国家/地区")}
                disabled={isSubmitting}
                className="bg-white/5 border-white/10 focus-visible:border-white/25 focus-visible:ring-0 text-foreground placeholder:text-muted-foreground/60"
              />
            </Field>

            <Field label={t("Phone", "手机")} error={errors.phone}>
              <Input
                type="tel"
                value={form.phone}
                onChange={(e) => setField("phone", e.target.value)}
                placeholder={t("+1 234 567 8900", "+86 123 4567 8900")}
                disabled={isSubmitting}
                className="bg-white/5 border-white/10 focus-visible:border-white/25 focus-visible:ring-0 text-foreground placeholder:text-muted-foreground/60"
              />
            </Field>
          </div>

          <Field label={t("Email", "邮箱")} error={errors.email}>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setField("email", e.target.value)}
              placeholder={t("you@company.com", "you@company.com")}
              disabled={isSubmitting}
              className="bg-white/5 border-white/10 focus-visible:border-white/25 focus-visible:ring-0 text-foreground placeholder:text-muted-foreground/60"
            />
          </Field>

          {/* ── Section 2: Your needs ── */}
          <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground pt-2">
            {t("Your Needs", "你的需求")}
          </p>

          {/* Services multi-select */}
          <Field label={t("Services of Interest", "希望了解的服务")} required error={errors.services}>
            <Popover open={servicesOpen} onOpenChange={setServicesOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  disabled={isSubmitting}
                  className="flex h-10 w-full items-center justify-between rounded-md border border-white/10 bg-white/5 px-3 py-2 text-sm text-foreground hover:bg-white/8 transition-colors disabled:opacity-50"
                >
                  <span
                    className={
                      form.services.length === 0
                        ? "text-muted-foreground/60 truncate"
                        : "truncate"
                    }
                  >
                    {servicesLabel}
                  </span>
                  <ChevronDown size={14} className="shrink-0 ml-2 text-muted-foreground" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="landing-v2 w-[var(--radix-popover-trigger-width)] p-2 bg-background border border-border rounded-xl z-[200]"
              >
                <div className="space-y-1">
                  {SERVICES.map((s) => (
                    <label
                      key={s.value}
                      className="flex items-center gap-3 px-2 py-1.5 rounded-lg cursor-pointer hover:bg-white/5 transition-colors"
                    >
                      <Checkbox
                        checked={form.services.includes(s.value)}
                        onCheckedChange={() => toggleService(s.value)}
                        disabled={isSubmitting}
                        className="border-white/30 data-[state=checked]:bg-[hsl(var(--brand))] data-[state=checked]:border-[hsl(var(--brand))]"
                      />
                      <span className="text-sm text-foreground select-none">{t(s.en, s.zh)}</span>
                      {form.services.includes(s.value) && (
                        <Check size={12} className="ml-auto brand-text" />
                      )}
                    </label>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </Field>

          {/* Other service remark */}
          {showOtherRemark && (
            <Field label={t("Other Service Remark", "其他服务说明")} error={errors.otherServiceRemark}>
              <Input
                value={form.otherServiceRemark}
                onChange={(e) => setField("otherServiceRemark", e.target.value)}
                placeholder={t("Please describe your needs", "请描述您的需求")}
                disabled={isSubmitting}
                className="bg-white/5 border-white/10 focus-visible:border-white/25 focus-visible:ring-0 text-foreground placeholder:text-muted-foreground/60"
              />
            </Field>
          )}

          {/* Budget radio */}
          <Field label={t("Budget (CNY)", "预算（人民币）")} required error={errors.budget}>
            <RadioGroup
              value={form.budget}
              onValueChange={(v) => {
                setForm((prev) => ({ ...prev, budget: v as CustomerProfileBudget }));
                setErrors((prev) => ({ ...prev, budget: undefined }));
              }}
              disabled={isSubmitting}
              className="flex flex-wrap gap-2"
            >
              {BUDGETS.map((b) => (
                <label
                  key={b.value}
                  className={`flex items-center gap-2 cursor-pointer px-3 py-1.5 rounded-lg border text-sm transition-colors ${
                    form.budget === b.value
                      ? "border-[hsl(var(--brand))] brand-text bg-[hsl(var(--brand))]/10"
                      : "border-white/10 text-muted-foreground hover:border-white/25 hover:text-foreground"
                  } ${isSubmitting ? "opacity-50" : ""}`}
                >
                  <RadioGroupItem value={b.value} className="sr-only" />
                  {b.label}
                </label>
              ))}
            </RadioGroup>
          </Field>

          <button
            type="submit"
            disabled={isSubmitting}
            className="btn-primary w-full justify-center mt-2 disabled:opacity-50"
          >
            {isSubmitting ? (
              <>
                <Loader2 size={16} className="animate-spin mr-2" />
                {t("Submitting...", "提交中...")}
              </>
            ) : (
              t("Submit", "提交")
            )}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// --- helper ---

function Field({
  label,
  required,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-foreground">
        {label}
        {required && <span className="text-destructive ml-0.5">*</span>}
      </label>
      {children}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}